"use strict";
// Copyright 2017-2018 Stratumn SAS. All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var canonicaljson_1 = require("@stratumn/canonicaljson");
var b64 = __importStar(require("base64-js"));
var buffer_1 = require("buffer");
var fast_sha256_1 = __importDefault(require("fast-sha256"));
var jmespath_1 = require("jmespath");
var js_base64_1 = require("js-base64");
var constants = __importStar(require("./const"));
var errors = __importStar(require("./errors"));
var process_1 = require("./process");
var chainscript_pb_1 = require("./proto/chainscript_pb");
var ref_1 = require("./ref");
var segment_1 = require("./segment");
var signature_1 = require("./signature");
/**
 * Deserialize a link.
 * @param linkBytes encoded bytes.
 * @returns the deserialized link.
 */
function deserialize(linkBytes) {
    var pbLink = chainscript_pb_1.stratumn.chainscript.Link.decode(linkBytes);
    return new Link(pbLink);
}
exports.deserialize = deserialize;
/**
 * Convert a plain object to a link.
 * @param link plain object.
 */
function fromObject(link) {
    return new Link(chainscript_pb_1.stratumn.chainscript.Link.fromObject(link));
}
exports.fromObject = fromObject;
/**
 * A link is the immutable part of a segment.
 * A link contains all the data that represents a process' step.
 */
var Link = /** @class */ (function () {
    function Link(link) {
        this.link = link;
    }
    /**
     * A link is usually created as a result of an action.
     * @returns the link's action.
     */
    Link.prototype.action = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        return meta.action || "";
    };
    /**
     * Add a signature to the link.
     * This will validate the signature before adding it.
     * @param signature link signature.
     */
    Link.prototype.addSignature = function (signature) {
        signature.validate(this);
        var s = new chainscript_pb_1.stratumn.chainscript.Signature();
        s.version = signature.version();
        s.payloadPath = signature.payloadPath();
        s.publicKey = signature.publicKey();
        s.signature = signature.signature();
        this.link.signatures.push(s);
    };
    /**
     * The client id allows segment receivers to figure out how the segment was
     * encoded and can be decoded.
     * @returns the link's client id.
     */
    Link.prototype.clientId = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        return meta.clientId || "";
    };
    /**
     * The link data (business logic details about the execution of a process step).
     * @returns the object containing the link details.
     */
    Link.prototype.data = function () {
        this.verifyCompatibility();
        var linkData = this.link.data;
        if (!linkData || linkData.length === 0) {
            return undefined;
        }
        switch (this.version()) {
            case constants.LINK_VERSION_1_0_0:
                return canonicaljson_1.parse(js_base64_1.Base64.decode(b64.fromByteArray(linkData)));
            default:
                throw errors.ErrLinkVersionUnknown;
        }
    };
    /**
     * Serialize the link and compute a hash of the resulting bytes.
     * The serialization and hashing algorithm used depend on the link version.
     * @returns the hash bytes.
     */
    Link.prototype.hash = function () {
        switch (this.version()) {
            case constants.LINK_VERSION_1_0_0:
                var linkBytes = chainscript_pb_1.stratumn.chainscript.Link.encode(this.link).finish();
                return fast_sha256_1.default(linkBytes);
            default:
                throw errors.ErrLinkVersionUnknown;
        }
    };
    /**
     * A link always belongs to a specific process map.
     * @returns the link's map id.
     */
    Link.prototype.mapId = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        return meta.mapId || "";
    };
    /**
     * The link metadata can contain a custom object.
     * @returns the object containing the link metadata details.
     */
    Link.prototype.metadata = function () {
        this.verifyCompatibility();
        var linkMetadata = this.link.meta.data;
        if (!linkMetadata || linkMetadata.length === 0) {
            return undefined;
        }
        switch (this.version()) {
            case constants.LINK_VERSION_1_0_0:
                return canonicaljson_1.parse(js_base64_1.Base64.decode(b64.fromByteArray(linkMetadata)));
            default:
                throw errors.ErrLinkVersionUnknown;
        }
    };
    /**
     * Maximum number of children a link is allowed to have.
     * This is set to -1 if the link is allowed to have as many children as it
     * wants.
     * @returns the maximum number of children allowed.
     */
    Link.prototype.outDegree = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        return meta.outDegree || 0;
    };
    /**
     * A link can have a parent, referenced by its link hash.
     * @returns the parent link hash.
     */
    Link.prototype.prevLinkHash = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        if (!meta.prevLinkHash) {
            return new Uint8Array(0);
        }
        // We re-wrap inside a Uint8Array because it might have become a Buffer if
        // converted from a plain Javascript object.
        return new Uint8Array(meta.prevLinkHash);
    };
    /**
     * The priority can be used to order links.
     * @returns the link's priority.
     */
    Link.prototype.priority = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        return meta.priority || 0;
    };
    /**
     * A link always belong to an instance of a process.
     * @returns the link's process name.
     */
    Link.prototype.process = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        var process = meta.process;
        if (!process) {
            throw errors.ErrLinkProcessMissing;
        }
        return new process_1.Process(process.name || "", process.state || "");
    };
    /**
     * A link can contain references to other links.
     * @returns referenced links.
     */
    Link.prototype.refs = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        if (!meta.refs) {
            return new Array(0);
        }
        return meta.refs.map(function (ref) {
            return new ref_1.LinkReference(ref.linkHash || new Uint8Array(0), ref.process || "");
        });
    };
    /**
     * Create a segment from the link.
     * @returns the segment wrapping the link.
     */
    Link.prototype.segmentify = function () {
        var segment = new chainscript_pb_1.stratumn.chainscript.Segment();
        segment.link = this.link;
        return new segment_1.Segment(segment);
    };
    /**
     * Serialize the link.
     * @returns link bytes.
     */
    Link.prototype.serialize = function () {
        return chainscript_pb_1.stratumn.chainscript.Link.encode(this.link).finish();
    };
    /**
     * Set the given object as the link's data.
     * @param data custom data to save with the link.
     */
    Link.prototype.setData = function (data) {
        this.verifyCompatibility();
        switch (this.version()) {
            case constants.LINK_VERSION_1_0_0:
                this.link.data = b64.toByteArray(js_base64_1.Base64.encode(canonicaljson_1.stringify(data)));
                return;
            default:
                throw errors.ErrLinkVersionUnknown;
        }
    };
    /**
     * Set the given object as the link's metadata.
     * @param data custom data to save with the link metadata.
     */
    Link.prototype.setMetadata = function (data) {
        this.verifyCompatibility();
        if (!this.link.meta) {
            throw errors.ErrLinkMetaMissing;
        }
        switch (this.version()) {
            case constants.LINK_VERSION_1_0_0:
                this.link.meta.data = b64.toByteArray(js_base64_1.Base64.encode(canonicaljson_1.stringify(data)));
                return;
            default:
                throw errors.ErrLinkVersionUnknown;
        }
    };
    /**
     * Sign configurable parts of the link with the current signature version.
     * The payloadPath is used to select what parts of the link need to be signed
     * with the given private key. If no payloadPath is provided, the whole link
     * is signed.
     * The signature is added to the link's signature list.
     * @param key private key in PEM format (generated by @stratumn/js-crypto).
     * @param payloadPath link parts that should be signed.
     */
    Link.prototype.sign = function (key, payloadPath) {
        var signature = signature_1.signLink(key, this, payloadPath);
        var s = new chainscript_pb_1.stratumn.chainscript.Signature();
        s.version = signature.version();
        s.payloadPath = signature.payloadPath();
        s.publicKey = signature.publicKey();
        s.signature = signature.signature();
        this.link.signatures.push(s);
    };
    /**
     * @returns the link's signatures (if any).
     */
    Link.prototype.signatures = function () {
        return this.link.signatures.map(function (s) {
            return new signature_1.Signature(s);
        });
    };
    /**
     * Compute the bytes that should be signed.
     * @argument version impacts how those bytes are computed.
     * @argument payloadPath parts of the link that should be signed.
     * @returns bytes to be signed.
     */
    Link.prototype.signedBytes = function (version, payloadPath) {
        switch (version) {
            case constants.SIGNATURE_VERSION_1_0_0:
                if (!payloadPath) {
                    payloadPath = "[version,data,meta]";
                }
                var payloadData = jmespath_1.search(this.link.toJSON(), payloadPath);
                var jsonData = canonicaljson_1.stringify(payloadData);
                var payloadBytes = buffer_1.Buffer.from(jsonData, "utf8");
                return fast_sha256_1.default(payloadBytes);
            default:
                throw errors.ErrSignatureVersionUnknown;
        }
    };
    /**
     * (Optional) A link can be interpreted as a step in a process.
     * @returns the corresponding process step.
     */
    Link.prototype.step = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        return meta.step || "";
    };
    /**
     * (Optional) A link can be tagged.
     * Tags are useful to filter link search results.
     * @returns link tags.
     */
    Link.prototype.tags = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        return meta.tags || [];
    };
    /**
     * Convert to a plain object.
     * @argument conversionOpts specify how to convert certain types.
     * @returns a plain object.
     */
    Link.prototype.toObject = function (conversionOpts) {
        return chainscript_pb_1.stratumn.chainscript.Link.toObject(this.link, conversionOpts);
    };
    /**
     * Validate checks for errors in a link.
     */
    Link.prototype.validate = function () {
        var _this = this;
        if (!this.link.version) {
            throw errors.ErrLinkVersionMissing;
        }
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        if (!meta.mapId) {
            throw errors.ErrLinkMapIdMissing;
        }
        if (!meta.process || !meta.process.name) {
            throw errors.ErrLinkProcessMissing;
        }
        this.verifyCompatibility();
        this.refs().map(function (r) {
            if (!r.process) {
                throw errors.ErrLinkProcessMissing;
            }
            if (!r.linkHash || r.linkHash.length === 0) {
                throw errors.ErrLinkHashMissing;
            }
        });
        this.signatures().map(function (s) {
            s.validate(_this);
        });
    };
    /**
     * The link version is used to properly serialize and deserialize it.
     * @returns the link version.
     */
    Link.prototype.version = function () {
        return this.link.version;
    };
    /**
     * Check if the link is compatible with the current library.
     * If not compatible, will throw an exception.
     */
    Link.prototype.verifyCompatibility = function () {
        var meta = this.link.meta;
        if (!meta) {
            throw errors.ErrLinkMetaMissing;
        }
        var clientId = meta.clientId;
        if (!clientId) {
            throw errors.ErrLinkClientIdUnkown;
        }
        var compatibleClients = [
            constants.ClientId,
            "github.com/stratumn/go-chainscript",
            "github.com/stratumn/java-chainscript",
            "github.com/stratumn/csharp-chainscript"
        ];
        if (compatibleClients.indexOf(clientId) < 0) {
            throw errors.ErrLinkClientIdUnkown;
        }
    };
    return Link;
}());
exports.Link = Link;
//# sourceMappingURL=link.js.map